import { StandardEvents } from '@shopify/events';

/**
 * Shows / hides the "Notify me" button for the selected variant.
 *
 * At Horizon v2 this lived in our patched `product-form.js` (`#onVariantUpdate`). v4 owns that
 * file and the theme-owned variant-update event class is gone, so it is now a listener on the
 * standard `shopify:product:select` event, whose deferred promise carries the same `resource`.
 *
 * The lenses picker drives the same element itself (a lens PDP never fires a product-select), so
 * the two never fight.
 */
document.addEventListener(StandardEvents.productSelect, (event) => {
  const target = /** @type {any} */ (event).target;
  const section = target instanceof Element ? target.closest('.shopify-section') : null;
  const button = /** @type {HTMLElement | null} */ (section?.querySelector('[id^="NotifyMeButton-"]') ?? null);
  if (!button) return;

  const promise = /** @type {Promise<any> | undefined} */ (/** @type {any} */ (event).promise);
  if (!promise) return;

  promise.then(({ detail }) => {
    // Leave the button alone on a failed fetch — `swati-variant-guard.js` restores the last
    // confirmed state, including this button, and a 429 body has no variant-picker in it.
    if (!detail?.html?.querySelector?.('variant-picker')) return;
    button.style.display = detail.resource == null || detail.resource.available === false ? 'block' : 'none';
  }, () => {});
});

class NotifyMeHandler extends HTMLElement {
  connectedCallback() {
    this.#bind();
  }

  #bind() {
    const root = this.closest('dialog') || this;
    const form = root.querySelector('.notify-modal-form');
    const submitButton = root.querySelector('.notify-me__submit');
    const spinner = root.querySelector('.loading__spinner');
    const successContent = root.querySelector('#notifyMeSuccess');
    const content = root.querySelector('#notifyMeContent');
    const closeButton = root.querySelector('.notify-me__close');

    if (!form || !submitButton || !spinner) return;

    closeButton?.addEventListener('click', () => {
      if (successContent && content) {
        successContent.style.display = 'none';
        content.style.display = 'block';
      }
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      submitButton.classList.add('loading');
      spinner.style.display = 'inline-block';

      const email = /** @type {HTMLInputElement} */ (form.elements.namedItem('notifyMeEmail'))?.value;
      const klaviyoId = /** @type {HTMLInputElement} */ (form.elements.namedItem('klaviyoId'))?.value;
      const variantId = /** @type {HTMLInputElement} */ (form.elements.namedItem('variant'))?.value;
      const isPushAllowed = /** @type {HTMLInputElement} */ (form.elements.namedItem('push'))?.checked;

      if (!email || !klaviyoId || !variantId) {
        submitButton.classList.remove('loading');
        spinner.style.display = 'none';
        return;
      }

      try {
        await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${klaviyoId}`, {
          method: 'POST',
          headers: {
            revision: '2024-02-15',
            accept: 'application/json',
            'content-type': 'application/json',
            'x-klaviyo-onsite': '1',
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email },
                  },
                },
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: `$shopify:::$default:::${variantId}`,
                  },
                },
              },
            },
          }),
        });

        submitButton.classList.remove('loading');
        spinner.style.display = 'none';
        if (successContent && content) {
          successContent.style.display = 'block';
          content.style.display = 'none';
        }

        const pushowl = /** @type {any} */ (window)['pushowl'];
        if (isPushAllowed && pushowl && typeof pushowl['registerForEvent'] === 'function') {
          pushowl['registerForEvent']('modal-dialog', false);
        }
      } catch (error) {
        submitButton.classList.remove('loading');
        spinner.style.display = 'none';
        console.error('Failed to subscribe to back in stock', error);
      }
    });
  }
}

if (!customElements.get('notify-me-handler')) customElements.define('notify-me-handler', NotifyMeHandler);


